/**
 * The `ch-tabular-grid-virtual-scroller` component that displays a subset of items.
 * It optimizes the rendering of large data sets by only rendering the items that are currently visible on the screen
 * based on the viewport size and scroll position.
 */
export class ChTabularGridVirtualScroller {
    constructor() {
        this.hasGridScroll = false;
        this.hasWindowScroll = false;
        this.browserHeight = document.documentElement.clientHeight;
        this.headerHeight = undefined;
        this.rowsHeight = 0;
        this.rowHeight = 0;
        this.virtualHeight = 0;
        this.maxViewPortItems = 7;
        this.percentScroll = 0;
        this.startIndex = null;
        this.isScrolling = false;
        this.items = undefined;
        this.itemsCount = undefined;
        this.viewPortItems = undefined;
    }
    gridEl;
    gridLayoutEl;
    resizeObserver;
    el;
    hasGridScrollHandler() {
        this.unobserveScroll();
        this.observeScroll();
        this.definePercentScroll();
    }
    hasWindowScrollHandler() {
        this.gridLayoutEl.style.overflowX = this.hasWindowScroll
            ? "visible"
            : "auto";
        this.unobserveScroll();
        this.observeScroll();
        this.definePercentScroll();
    }
    browserHeightHandler() {
        this.defineMaxViewPortItems();
        this.defineVirtualHeight();
    }
    headerHeightHandler() {
        this.el.style.top = `${this.headerHeight}px`;
    }
    rowsHeightHandler() {
        this.defineHeaderHeight();
        this.defineRowHeight();
    }
    rowHeightHandler() {
        this.defineMaxViewPortItems();
        this.defineVirtualHeight();
    }
    virtualHeightHandler() {
        this.unobserveScroll();
        this.unobserveResize();
        this.gridLayoutEl.style.setProperty("--ch-tabular-grid-virtual-scroller-height", `${this.virtualHeight}px`);
        if (Math.ceil(this.percentScroll) !== 100) {
            if (this.hasGridScroll) {
                this.gridLayoutEl.scrollTop =
                    this.percentScroll /
                        (100 /
                            (this.gridLayoutEl.scrollHeight - this.gridLayoutEl.clientHeight));
            }
            else if (this.hasWindowScroll) {
                window.scrollY =
                    this.percentScroll /
                        (100 / (this.gridEl.clientHeight - this.browserHeight));
            }
        }
        this.observeScroll();
        this.observeResize();
    }
    maxViewPortItemsHandler() {
        this.defineViewPortItems();
    }
    percentScrollHandler() {
        this.defineStartIndex();
    }
    startIndexHandler() {
        this.defineViewPortItems();
    }
    isScrollingHandler() {
        if (!this.isScrolling) {
            this.defineMaxViewPortItems();
            this.defineVirtualHeight();
        }
    }
    itemsHandler() {
        if (!this.startIndex === null) {
            this.defineStartIndex();
        }
        else {
            this.defineViewPortItems();
            this.defineVirtualHeight();
        }
    }
    itemsCountHandler() {
        if (!this.startIndex === null) {
            this.defineStartIndex();
        }
        else {
            this.defineViewPortItems();
            this.defineVirtualHeight();
        }
    }
    viewPortItemsHandler() {
        this.viewPortItemsChanged.emit();
    }
    /**
     *Event emitted when the list of visible items in the grid changes.
     */
    viewPortItemsChanged;
    componentWillLoad() {
        this.gridLayoutEl = this.el.assignedSlot.parentElement;
        this.gridEl = this.el.closest("ch-tabular-grid");
        this.resizeObserver = new ResizeObserver(this.resizeHandler);
        this.observeScroll();
        this.observeResize();
    }
    observeScroll() {
        let viewport;
        if (this.hasGridScroll) {
            viewport = this.gridLayoutEl;
        }
        else if (this.hasWindowScroll) {
            viewport = document;
        }
        viewport?.addEventListener("scroll", this.scrollHandler, {
            passive: true
        });
        viewport?.addEventListener("scrollend", this.scrollEndHandler, {
            passive: true
        });
    }
    observeResize() {
        this.resizeObserver.observe(this.el);
        this.resizeObserver.observe(this.gridEl);
        this.resizeObserver.observe(document.documentElement);
        this.resizeObserver.observe(document.body);
    }
    unobserveScroll() {
        document.removeEventListener("scroll", this.scrollHandler);
        this.gridLayoutEl.removeEventListener("scroll", this.scrollHandler);
    }
    unobserveResize() {
        this.resizeObserver.unobserve(this.el);
        this.resizeObserver.unobserve(this.gridEl);
        this.resizeObserver.unobserve(document.documentElement);
        this.resizeObserver.unobserve(document.body);
    }
    scrollHandler = () => {
        this.isScrolling = true;
        this.definePercentScroll();
    };
    scrollEndHandler = () => {
        this.isScrolling = false;
    };
    resizeHandler = (entries) => {
        entries.forEach(entry => {
            switch (entry.target) {
                case this.el:
                    this.rowsHeight = entry.contentRect.height;
                    break;
                case document.documentElement:
                case document.body:
                    this.browserHeight = document.documentElement.clientHeight;
                    break;
            }
        });
        this.defineHasScroll();
    };
    defineHasScroll() {
        this.hasGridScroll =
            this.gridLayoutEl.scrollHeight !== this.gridLayoutEl.clientHeight;
        this.hasWindowScroll =
            !this.hasGridScroll && this.gridEl.clientHeight > this.browserHeight;
    }
    defineHeaderHeight() {
        this.headerHeight = parseFloat(getComputedStyle(this.gridLayoutEl).gridTemplateRows);
    }
    defineRowHeight() {
        if (this.viewPortItems.length === 0) {
            this.rowHeight = 0;
        }
        else if (this.viewPortItems.length > 0 && this.percentScroll === 0) {
            this.rowHeight = this.rowsHeight / this.viewPortItems.length;
        }
        else {
            this.rowHeight = Math.min(this.rowHeight, this.rowsHeight / this.viewPortItems.length);
        }
    }
    defineMaxViewPortItems() {
        if (this.rowHeight === 0) {
            this.maxViewPortItems = 7;
        }
        else {
            this.maxViewPortItems = Math.ceil(this.browserHeight / this.rowHeight);
        }
    }
    defineVirtualHeight() {
        if (!this.isScrolling) {
            this.virtualHeight = this.items.length * this.rowHeight;
        }
    }
    defineViewPortItems() {
        this.viewPortItems = this.items.slice(this.startIndex, this.startIndex + this.maxViewPortItems);
        // this.viewPortItems = this.items.slice(
        //   Math.min(this.startIndex, this.items.length - this.maxViewPortItems),
        //   this.startIndex + this.maxViewPortItems
        // );
    }
    defineStartIndex() {
        const index = (this.percentScroll *
            Math.max(this.items.length - this.maxViewPortItems, 0)) /
            100;
        // const index = (this.percentScroll * (this.items.length - 1)) / 100;
        this.startIndex =
            this.percentScroll <= 50 ? Math.floor(index) : Math.ceil(index);
    }
    definePercentScroll() {
        let hiddenHeight = 0;
        let scrollPosition = 0;
        if (this.hasGridScroll) {
            hiddenHeight =
                this.gridLayoutEl.scrollHeight - this.gridLayoutEl.clientHeight;
            scrollPosition = this.gridLayoutEl.scrollTop;
        }
        else if (this.hasWindowScroll) {
            const gridRect = this.gridEl.getBoundingClientRect();
            hiddenHeight = this.gridEl.clientHeight - this.browserHeight;
            scrollPosition = Math.min(gridRect.top >= 0 ? 0 : gridRect.top * -1, hiddenHeight);
        }
        this.percentScroll =
            hiddenHeight > 0 ? (scrollPosition * 100) / hiddenHeight : 0;
    }
    static get is() { return "ch-tabular-grid-virtual-scroller"; }
    static get originalStyleUrls() {
        return {
            "$": ["tabular-grid-virtual-scroller.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tabular-grid-virtual-scroller.css"]
        };
    }
    static get properties() {
        return {
            "items": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "any[]",
                    "resolved": "any[]",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The list of items to be rendered in the grid."
                }
            },
            "itemsCount": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The number of elements in the items list.\nUse if the list changes, without recreating the array."
                },
                "attribute": "items-count",
                "reflect": false
            },
            "viewPortItems": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "any[]",
                    "resolved": "any[]",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The list of items to display within the current viewport."
                }
            }
        };
    }
    static get states() {
        return {
            "hasGridScroll": {},
            "hasWindowScroll": {},
            "browserHeight": {},
            "headerHeight": {},
            "rowsHeight": {},
            "rowHeight": {},
            "virtualHeight": {},
            "maxViewPortItems": {},
            "percentScroll": {},
            "startIndex": {},
            "isScrolling": {}
        };
    }
    static get events() {
        return [{
                "method": "viewPortItemsChanged",
                "name": "viewPortItemsChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the list of visible items in the grid changes."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "hasGridScroll",
                "methodName": "hasGridScrollHandler"
            }, {
                "propName": "hasWindowScroll",
                "methodName": "hasWindowScrollHandler"
            }, {
                "propName": "browserHeight",
                "methodName": "browserHeightHandler"
            }, {
                "propName": "headerHeight",
                "methodName": "headerHeightHandler"
            }, {
                "propName": "rowsHeight",
                "methodName": "rowsHeightHandler"
            }, {
                "propName": "rowHeight",
                "methodName": "rowHeightHandler"
            }, {
                "propName": "virtualHeight",
                "methodName": "virtualHeightHandler"
            }, {
                "propName": "maxViewPortItems",
                "methodName": "maxViewPortItemsHandler"
            }, {
                "propName": "percentScroll",
                "methodName": "percentScrollHandler"
            }, {
                "propName": "startIndex",
                "methodName": "startIndexHandler"
            }, {
                "propName": "isScrolling",
                "methodName": "isScrollingHandler"
            }, {
                "propName": "items",
                "methodName": "itemsHandler"
            }, {
                "propName": "itemsCount",
                "methodName": "itemsCountHandler"
            }, {
                "propName": "viewPortItems",
                "methodName": "viewPortItemsHandler"
            }];
    }
}
